import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, Compass } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { PushBanner } from "@/components/PushBanner";
import { FeedCard } from "@/components/FeedCard";
import { PostComposer } from "@/components/PostComposer";
import { MyMosqueCard } from "@/components/MyMosqueCard";
import { useLang } from "@/lib/i18n";
import { toHijri } from "@/lib/hijri";
import api from "@/lib/api";

export default function Feed() {
  const { t, lang } = useLang();
  const [feed, setFeed] = useState(null);
  const [assocs, setAssocs] = useState([]);
  const [hijriOffset, setHijriOffset] = useState(0);
  const today = new Date();
  const hijri = toHijri(today, hijriOffset);
  const locale = lang === "fr" ? "fr-FR" : "en-US";

  const loadAssocs = () =>
    api.get("/associations").then((r) => {
      setAssocs(r.data);
      if (r.data[0]) setHijriOffset(r.data[0].hijri_offset || 0);
    }).catch(() => {});

  const loadFeed = () => {
    api.get("/feed").then(({ data }) => {
      setFeed(data);
      if (data.hijri_offset !== undefined) setHijriOffset(data.hijri_offset || 0);
      if (data.no_memberships) loadAssocs();
    }).catch(() => {
      setFeed({ items: [], no_memberships: true });
      loadAssocs();
    });
  };

  useEffect(() => { loadFeed(); }, []);

  const myMosques = feed?.mosques?.filter((m) => m.followed) || [];
  const otherMosques = feed?.mosques?.filter((m) => !m.followed) || [];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <PushBanner />
      <div className="max-w-xl mx-auto px-4 py-6">
        <div className="flex items-baseline justify-between mb-6">
          <h1 className="font-heading font-extrabold text-xl tracking-tight" data-testid="feed-title">{t("feed")}</h1>
          <p className="text-xs font-mono-data text-muted-foreground text-right">
            <span className="capitalize">{today.toLocaleDateString(locale, { day: "numeric", month: "short" })}</span> · {hijri.label} AH
          </p>
        </div>

        {myMosques.map((m) => (
          <MyMosqueCard key={m.id} mosque={m} />
        ))}

        <PostComposer onPosted={loadFeed} />

        {otherMosques.length > 0 && (
          <div className="mb-6" data-testid="feed-mosques-row">
            <p className="text-[11px] font-mono-data uppercase tracking-wider text-muted-foreground mb-3">{myMosques.length > 0 ? t("other_mosques") : t("mosques")}</p>
            <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1">
              {otherMosques.map((m) => (
                <Link key={m.id} to={`/${m.association_slug}/mosques/${m.id}`} data-testid="feed-mosque-story" className="flex flex-col items-center gap-1.5 shrink-0 w-[68px] group">
                  <div className={`h-14 w-14 rounded-full p-[2px] transition-transform group-hover:scale-105 ${m.followed ? "ring-2 ring-accent ring-offset-2 ring-offset-background" : "ring-1 ring-border"}`}>
                    {m.photo ? (
                      <img src={m.photo} alt={m.name} className="h-full w-full rounded-full object-cover" />
                    ) : (
                      <div className="h-full w-full rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ background: m.color }}>
                        {m.name?.slice(0, 2).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <span className="text-[10px] text-center leading-tight truncate w-full text-muted-foreground group-hover:text-foreground transition-colors">{m.name}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {feed === null && (
          <div className="space-y-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="border border-border rounded-xl bg-card h-44 animate-pulse" />
            ))}
          </div>
        )}

        {feed?.no_memberships && (
          <div data-testid="feed-discover">
            <div className="border border-dashed border-border rounded-xl p-8 text-center">
              <Compass className="h-8 w-8 text-accent mx-auto mb-3" />
              <p className="font-heading font-bold">{t("no_feed_title")}</p>
              <p className="text-sm text-muted-foreground mt-1.5">{t("no_feed_text")}</p>
            </div>
            <h2 className="font-heading font-bold text-sm mt-8 mb-4 uppercase tracking-wider text-muted-foreground">{t("explore_assocs")}</h2>
            <div className="space-y-3">
              {assocs.map((a) => (
                <Link key={a.id} to={`/${a.slug}`} data-testid="discover-assoc-card" className="border border-border rounded-xl bg-card p-4 flex items-center gap-4 hover-lift block">
                  <div className="h-11 w-11 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0" style={{ background: a.primary_color || "#2563EB" }}>
                    {a.name?.slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-heading font-bold truncate">{a.name}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                      {a.city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{a.city}</span>}
                      <span>{a.members_count} {t("members")}</span>
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {feed && !feed.no_memberships && (
          <div className="space-y-5" data-testid="feed-list">
            {feed.items.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-16 border border-dashed border-border rounded-xl">{t("no_feed_text")}</p>
            )}
            {feed.items.map((item) => (
              <FeedCard key={`${item.type}-${item.id}`} item={item} onDelete={(id) => setFeed((f) => ({ ...f, items: f.items.filter((x) => x.id !== id) }))} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
